import React, { createContext, useContext, useState } from 'react';
import { Box, Button, Drawer, IconButton, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HideIcon from '@material-ui/icons/FullscreenExit';
import { FlexBox } from '../../../global/FlexBox';
import { Confirm } from '../../../userManagement/components/Confirm';
import {
  DrawerHeader,
  DrawerHeaderCenter,
  DrawerHeaderSide,
  RequiredFieldNote,
  drawerPaperStyles,
} from './styles';
import { DiamDrawerProps } from './types';

const DiamDrawerCloseContext = createContext<(() => void) | null>(null);

export function useDiamDrawerClose() {
  const requestClose = useContext(DiamDrawerCloseContext);
  if (!requestClose) {
    throw new Error('useDiamDrawerClose must be used within DiamDrawer');
  }
  return requestClose;
}

interface DiamDrawerCancelButtonProps {
  disabled?: boolean;
}

export function DiamDrawerCancelButton({
  disabled,
}: DiamDrawerCancelButtonProps) {
  const requestClose = useDiamDrawerClose();

  return (
    <Button
      variant="text"
      color="primary"
      onClick={requestClose}
      disabled={disabled}
    >
      Cancel
    </Button>
  );
}

export function DiamDrawer({
  isOpen,
  handleDrawerClose,
  children,
  hasHide,
  title,
  sectionTitle,
  confirmOnClose = false,
  confirmTitle = 'Are you sure you want to cancel?',
  confirmContent = 'Any unsaved changes will be lost.',
  confirmButtonText = 'Cancel',
  cancelButtonText = 'Dismiss',
  handleHide,
  headerCenter,
}: DiamDrawerProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleClose = () => {
    if (confirmOnClose) {
      setIsConfirmOpen(true);
    } else {
      handleDrawerClose();
    }
  };

  const handleMinimize = () => {
    handleHide?.();
    handleDrawerClose();
  };

  return (
    <>
      <Drawer
        open={isOpen}
        anchor="right"
        PaperProps={{
          style: drawerPaperStyles,
        }}
      >
        <DrawerHeader>
          <DrawerHeaderSide>
            {hasHide && (
              <Button
                type="button"
                startIcon={<HideIcon color="primary" />}
                variant="text"
                color="primary"
                onClick={handleMinimize}
              >
                Hide
              </Button>
            )}
          </DrawerHeaderSide>
          <DrawerHeaderCenter>{headerCenter}</DrawerHeaderCenter>
          <DrawerHeaderSide style={{ justifyContent: 'flex-end' }}>
            <IconButton type="button" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DrawerHeaderSide>
        </DrawerHeader>
        {title && (
          <>
            <Box mb={3} mt={3}>
              <Typography variant="h3">{title}</Typography>
            </Box>
            <FlexBox
              mb={6}
              sx={{ justifyContent: 'space-between', width: '100%' }}
            >
              {sectionTitle && (
                <Typography variant="h6">{sectionTitle}</Typography>
              )}
              <RequiredFieldNote variant="caption">
                *denotes required field
              </RequiredFieldNote>
            </FlexBox>
          </>
        )}
        <Box>
          <DiamDrawerCloseContext.Provider value={handleClose}>
            {children}
          </DiamDrawerCloseContext.Provider>
        </Box>
      </Drawer>
      {confirmOnClose && (
        <Confirm
          isOpen={isConfirmOpen}
          title={confirmTitle}
          content={confirmContent}
          confirm={confirmButtonText}
          cancel={cancelButtonText}
          onConfirm={() => {
            setIsConfirmOpen(false);
            handleDrawerClose();
          }}
          onClose={() => setIsConfirmOpen(false)}
        />
      )}
    </>
  );
}
